import { useEffect, useRef, useState } from "react";

import { replaceLocation } from "@/utils/auth0/resetPasswordSuccessRedirect";

export const SUCCESS_PRESENTATION_MS = 4000;

export const SUCCESS_RESOLVE_TIMEOUT_MS = 8000;

export interface SuccessAutoRedirectOptions {
  resolveFailed?: boolean;
}

/**
 * Show the success message for at least four seconds, then use the resolved
 * URL or the fallback if resolution failed. If still waiting after eight
 * seconds, use the fallback. Both deadlines are measured from mount.
 */
export function useSuccessAutoRedirect(
  url: string | null,
  fallbackUrl: string,
  options: SuccessAutoRedirectOptions = {},
): void {
  const { resolveFailed = false } = options;
  const [presentationReady, setPresentationReady] = useState(false);
  const [hasTimedOut, setHasTimedOut] = useState(false);
  const hasNavigatedRef = useRef(false);

  // Switching language on the success screen should not restart the notice
  // or extend the maximum wait. Both timers start when this screen appears.
  useEffect(() => {
    hasNavigatedRef.current = false;
    setPresentationReady(false);
    setHasTimedOut(false);

    const presentationId = window.setTimeout(() => {
      setPresentationReady(true);
    }, SUCCESS_PRESENTATION_MS);

    const timeoutId = window.setTimeout(() => {
      setHasTimedOut(true);
    }, SUCCESS_RESOLVE_TIMEOUT_MS);

    return () => {
      window.clearTimeout(presentationId);
      window.clearTimeout(timeoutId);
    };
  }, []);

  useEffect(() => {
    // The resolver can respond just after the eight-second fallback starts.
    // Ignore late updates so they cannot initiate a second navigation.
    if (!presentationReady || hasNavigatedRef.current) {
      return;
    }

    if (url) {
      hasNavigatedRef.current = true;
      replaceLocation(url);
      return;
    }

    if (!resolveFailed && !hasTimedOut) {
      return;
    }

    hasNavigatedRef.current = true;
    replaceLocation(fallbackUrl);
  }, [fallbackUrl, hasTimedOut, presentationReady, resolveFailed, url]);
}
